import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TbPlus } from 'react-icons/tb';

import DropdownSelect from '../forms/DropdownSelect';
import CreateCategoryModal from './CreateCategoryModal';
import { FinancialCategory } from '../../../types/bankAccount';
import * as bankAccountService from '../../../services/community/bankAccountService';

const CREATE_NEW_SENTINEL = '__CREATE_NEW__';

interface CategoryOption {
  label: string;
  value: number | string;
}

interface CategorySelectProps {
  value: number | null | undefined;
  setValue: (value: number | null) => void;
  categories: FinancialCategory[];
  onCategoryCreated?: (category: FinancialCategory) => void;
  placeholder?: string;
  isClearable?: boolean;
  autoFocus?: boolean;
  menuIsOpen?: boolean;
  showCreateOption?: boolean;
  stopClickPropagation?: boolean;
  onOpenChange?: (open: boolean) => void;
  onChange?: (value: number | null) => void;
}

function renderCategoryOption(option: CategoryOption): JSX.Element {
  if (option.value === CREATE_NEW_SENTINEL) {
    return (
      <span className="flex items-center gap-1 font-medium text-primary-700">
        <TbPlus className="h-4 w-4" />
        {option.label}
      </span>
    );
  }
  return <span>{option.label}</span>;
}

export default function CategorySelect({
  value,
  setValue,
  categories,
  onCategoryCreated,
  placeholder,
  isClearable = true,
  autoFocus = false,
  menuIsOpen = false,
  showCreateOption = true,
  stopClickPropagation = true,
  onOpenChange,
  onChange,
}: CategorySelectProps): JSX.Element {
  const { t } = useTranslation('', { keyPrefix: 'page.financial.categories' });
  const [createOpen, setCreateOpen] = useState(false);

  const options = useMemo<CategoryOption[]>(() => {
    const real = categories.map((c) => ({
      label: c.name,
      value: c.financialCategoryId,
    }));
    if (!showCreateOption) return real;
    return [
      ...real,
      {
        label: `+ ${t('createInlineOption') ?? 'Create new category'}`,
        value: CREATE_NEW_SENTINEL,
      },
    ];
  }, [categories, showCreateOption, t]);

  const handleChange = (next: number | string | undefined | null) => {
    if (next === CREATE_NEW_SENTINEL) {
      setCreateOpen(true);
      return;
    }
    const numeric = typeof next === 'number' ? next : null;
    setValue(numeric);
    onChange?.(numeric);
  };

  const handleCreated = (created: FinancialCategory) => {
    setValue(created.financialCategoryId);
    onChange?.(created.financialCategoryId);
    if (onCategoryCreated) {
      onCategoryCreated(created);
    }
  };

  return (
    <>
      {stopClickPropagation ? (
        <div onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
          <DropdownSelect<number | string>
            options={options}
            setValue={handleChange}
            value={value ?? undefined}
            placeholder={placeholder ?? t('choosePlaceholder')}
            isClearable={isClearable}
            autoFocus={autoFocus}
            menuIsOpen={menuIsOpen}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
            menuPosition="fixed"
            onMenuOpen={() => onOpenChange?.(true)}
            onMenuClose={() => onOpenChange?.(false)}
            formatOptionLabel={renderCategoryOption}
          />
        </div>
      ) : (
        <DropdownSelect<number | string>
          options={options}
          setValue={handleChange}
          value={value ?? undefined}
          placeholder={placeholder ?? t('choosePlaceholder')}
          isClearable={isClearable}
          autoFocus={autoFocus}
          menuIsOpen={menuIsOpen}
          menuPortalTarget={typeof document !== 'undefined' ? document.body : undefined}
          menuPosition="fixed"
          onMenuOpen={() => onOpenChange?.(true)}
          onMenuClose={() => onOpenChange?.(false)}
          formatOptionLabel={renderCategoryOption}
        />
      )}
      <CreateCategoryModal
        open={createOpen}
        setOpen={setCreateOpen}
        onCreated={handleCreated}
      />
    </>
  );
}

export async function fetchCategories(): Promise<FinancialCategory[]> {
  return bankAccountService.getCategories();
}